import { useEffect, useRef, useState } from "react"

import { Button } from "@coval/ui"
import { Download, Loader2, Printer } from "lucide-react"

import { downloadBlob } from "@/utils/download"

import {
  printsTaggedPdf,
  UNTAGGED_PRINT_NOTICE,
} from "../utils/notebookAccessibleExport"
import {
  notebookChartImages,
  type NotebookChartImages,
} from "../utils/notebookChartImages"
import {
  notebookPdfFilename,
  type NotebookExport,
} from "../utils/notebookExport"
import {
  NotebookExportCharts,
  waitForExportCharts,
} from "./NotebookExportCharts"

type ExportKind = "download" | "print"

export function NotebookDownloadButton({
  notebook,
  organizationSlug,
  workspaceId,
  disabled = false,
  onBusyChange,
}: {
  notebook: NotebookExport
  organizationSlug: string
  workspaceId: string
  disabled?: boolean
  onBusyChange?: (busy: boolean) => void
}) {
  const chartRef = useRef<HTMLDivElement>(null)
  const [busy, setBusy] = useState<ExportKind | null>(null)
  const [exportNotebook, setExportNotebook] = useState<NotebookExport | null>(
    null,
  )
  const busyRef = useRef(false)
  const mountedRef = useRef(false)
  const onBusyChangeRef = useRef(onBusyChange)
  onBusyChangeRef.current = onBusyChange
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (busyRef.current) {
        onBusyChangeRef.current?.(false)
      }
      busyRef.current = false
    }
  }, [])
  const run = async (kind: ExportKind) => {
    if (disabled || busyRef.current) {
      return
    }
    busyRef.current = true
    setBusy(kind)
    setExportNotebook(notebook)
    onBusyChange?.(true)
    setError("")
    try {
      const scope = {
        organizationSlug,
        workspaceId,
        origin: window.location.origin,
      }
      if (kind === "print") {
        setNotice(printsTaggedPdf() ? "" : UNTAGGED_PRINT_NOTICE)
        const [{ printNotebookExport }, captured] = await Promise.all([
          import("./NotebookPrintDocument"),
          // A chart that never renders prints its values table instead;
          // the charts that did render keep their images.
          waitForExportCharts(chartRef, notebook)
            .catch(() => chartRef.current)
            .then((root): Promise<NotebookChartImages> | NotebookChartImages =>
              root ? notebookChartImages(root, { skipFailed: true }) : {},
            ),
        ])
        if (mountedRef.current) {
          await printNotebookExport(notebook, captured, scope)
        }
      } else {
        const [{ buildNotebookPdf }, captured] = await Promise.all([
          import("./NotebookPdf"),
          waitForExportCharts(chartRef, notebook).then((root) =>
            notebookChartImages(root),
          ),
        ])
        const blob = await buildNotebookPdf(notebook, captured, scope)
        if (mountedRef.current) {
          downloadBlob(blob, notebookPdfFilename(notebook.title))
        }
      }
    } catch (failure) {
      if (mountedRef.current) {
        setError(
          failure instanceof Error
            ? failure.message
            : "Could not download the notebook. Try again.",
        )
      }
    } finally {
      if (mountedRef.current) {
        busyRef.current = false
        setBusy(null)
        setExportNotebook(null)
        onBusyChange?.(false)
      }
    }
  }
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex gap-1">
        <Button
          variant="ghost"
          disabled={disabled || !!busy}
          onClick={() => void run("print")}
        >
          {busy === "print" ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Printer />
          )}
          {busy === "print" ? "Preparing…" : "Accessible PDF"}
        </Button>
        <Button
          variant="secondary"
          disabled={disabled || !!busy}
          onClick={() => void run("download")}
        >
          {busy === "download" ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Download />
          )}
          {busy === "download" ? "Preparing PDF…" : "Download PDF"}
        </Button>
      </div>
      {busy && exportNotebook && (
        <NotebookExportCharts notebook={exportNotebook} ref={chartRef} />
      )}
      {notice && (
        <p role="status" className="max-w-sm text-sm text-coval-text-muted">
          {notice}
        </p>
      )}
      {error && (
        <p className="max-w-sm text-sm text-coval-danger-text" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
